import { cn, Stack, Tooltip, TooltipContent, TooltipTrigger } from 'component-library';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import {
  IndicatorName,
  PlotReportFactElement,
  PlotReportFactLabelNameEnum,
  ThreatType,
} from '@/api/rest/resources/types/fact';
import { GraphFact } from '@/components/Charts/types';
import { IndicatorCardProps } from '@/components/IndicatorCard/IndicatorCard';
import { NCCard } from '@/components/NCCard/NCCard';
import { HabitatIntactnessThreatsHistoricTable } from '@/components/Tables/HabitatIntactnessThreats';
import { Icon } from '@/components/widgets/a/Icon/Icon';
import {
  useOrganizationIndicatorGraphFact,
  useOrganizationIndicatorReportingFact,
} from '@/pages/shared/hooks/useOrganizationFact';

export const FACTS = {
  AREAS_HISTORIC: IndicatorName.threat_areas_historic_graph,
  AREA_PCT: IndicatorName.threat_areas_pct,
  FIVE_YEARS_PCT_CHANGE: IndicatorName.threat_areas_pct_previous_5y_pct_change,
} as const;

export const HabitatIntactnessThreatsTableCard = ({ headerProps, styles }: IndicatorCardProps) => {
  const { [FACTS.AREAS_HISTORIC]: tableData } = useOrganizationIndicatorGraphFact<GraphFact[]>(FACTS.AREAS_HISTORIC);

  // TODO: MVP-7638 unify queries into a single call
  const { [FACTS.AREA_PCT]: noThreatAreaFact, [FACTS.FIVE_YEARS_PCT_CHANGE]: noThreatArea5yPct } =
    useOrganizationIndicatorReportingFact<number>([FACTS.AREA_PCT, FACTS.FIVE_YEARS_PCT_CHANGE], undefined, {
      [PlotReportFactLabelNameEnum.threat_type]: ThreatType.no_threat_area,
    });
  const { [FACTS.AREA_PCT]: croplandAreaFact, [FACTS.FIVE_YEARS_PCT_CHANGE]: croplandArea5yPct } =
    useOrganizationIndicatorReportingFact<number>([FACTS.AREA_PCT, FACTS.FIVE_YEARS_PCT_CHANGE], undefined, {
      [PlotReportFactLabelNameEnum.threat_type]: ThreatType.cropland,
    });
  const { [FACTS.AREA_PCT]: deforestationAreaFact, [FACTS.FIVE_YEARS_PCT_CHANGE]: deforestationArea5yPct } =
    useOrganizationIndicatorReportingFact<number>([FACTS.AREA_PCT, FACTS.FIVE_YEARS_PCT_CHANGE], undefined, {
      [PlotReportFactLabelNameEnum.threat_type]: ThreatType.deforestation,
    });
  const { [FACTS.AREA_PCT]: builtAreaFact, [FACTS.FIVE_YEARS_PCT_CHANGE]: builtArea5yPct } =
    useOrganizationIndicatorReportingFact<number>([FACTS.AREA_PCT, FACTS.FIVE_YEARS_PCT_CHANGE], undefined, {
      [PlotReportFactLabelNameEnum.threat_type]: ThreatType.builtup,
    });
  const { [FACTS.AREA_PCT]: roadsAreaFact, [FACTS.FIVE_YEARS_PCT_CHANGE]: roadsArea5yPct } =
    useOrganizationIndicatorReportingFact<number>([FACTS.AREA_PCT, FACTS.FIVE_YEARS_PCT_CHANGE], undefined, {
      [PlotReportFactLabelNameEnum.threat_type]: ThreatType.roads,
    });

  const threatsAreaPct = [
    noThreatAreaFact,
    croplandAreaFact,
    deforestationAreaFact,
    builtAreaFact,
    roadsAreaFact,
  ].filter(Boolean) as PlotReportFactElement<number>[];

  const threatsArea5yPct = [
    noThreatArea5yPct,
    croplandArea5yPct,
    deforestationArea5yPct,
    builtArea5yPct,
    roadsArea5yPct,
  ].filter(Boolean) as PlotReportFactElement<number>[];

  const hasData = threatsAreaPct.length > 0 || threatsArea5yPct.length > 0 || tableData?.value?.length;

  if (!hasData) {
    return null;
  }

  return (
    <ThreatsTableCard headerProps={headerProps} styles={styles}>
      <HabitatIntactnessThreatsHistoricTable
        threatsTimeSeries={tableData?.value ?? []}
        threatsAreaPct={threatsAreaPct}
        threatsArea5yPct={threatsArea5yPct}
        className='mt-10'
      />
    </ThreatsTableCard>
  );
};

const ThreatsTableCard = ({ headerProps, styles, children }: IndicatorCardProps & { children: ReactNode }) => {
  const { t } = useTranslation();

  return (
    <NCCard className={cn('p-8', styles?.root)}>
      <Stack className='w-full flex flex-col items-center justify-items-center gap-4 lg:grid lg:grid-cols-[1fr_auto_1fr] lg:justify-items-start'>
        {headerProps?.title && (
          <Stack spacing={2} className='justify-self-start'>
            <span className='typography-overline uppercase'>{t('shared.ncaDetail.details.historicDevelopment')}</span>
            <span className='typography-h4'>
              {headerProps?.title}
              {headerProps?.headerTooltip && (
                <Tooltip>
                  <TooltipTrigger className='items-center'>
                    <Icon icon='ri-information-line' className='ml-1 text-active-54 typography-body1' />
                  </TooltipTrigger>
                  <TooltipContent className='whitespace-pre-wrap'>{headerProps?.headerTooltip}</TooltipContent>
                </Tooltip>
              )}
            </span>
          </Stack>
        )}
      </Stack>
      <Stack className='items-center sm:items-stretch' center>
        {children}
      </Stack>
    </NCCard>
  );
};
